import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const PhoneSystemDashboard = React.lazy(() => import("./PhoneSystemDashboard").then(m => ({ default: m.PhoneSystemDashboard })));
const BrowserCall = React.lazy(() => import("./BrowserCall").then(m => ({ default: m.BrowserCall })));
const CallRecordings = React.lazy(() => import("./CallRecordings").then(m => ({ default: m.CallRecordings })));
const PhoneNumbersList = React.lazy(() => import("./PhoneNumbersList").then(m => ({ default: m.PhoneNumbersList })));
const PhoneNumberAssignmentAdmin = React.lazy(() => import("./PhoneNumberAssignmentAdmin").then(m => ({ default: m.PhoneNumberAssignmentAdmin })));

const phoneSystemModuleConfig: ModuleConfig = {
  id: "phone-system",
  name: "Phone System",
  description: "Manage phone numbers, call recordings, and transcriptions",
  icon: "📞",
  version: "1.0.0",
  enabled: true,
  category: "communication",
  order: 10,
  routes: [
    { path: "/phone-system", component: PhoneSystemDashboard, permissionLevel: "view", useLayout: true },
    { path: "/phone-system/browser-call", component: BrowserCall, permissionLevel: "view", useLayout: true },
    { path: "/phone-system/recordings", component: CallRecordings, permissionLevel: "view", useLayout: true },
    { path: "/phone-system/numbers", component: PhoneNumbersList, permissionLevel: "edit", useLayout: true },
    { path: "/phone-system/assignments", component: PhoneNumberAssignmentAdmin, permissionLevel: "edit", useLayout: true },
  ],
  navigation: [
    {
      path: "/phone-system",
      label: "Dashboard",
      icon: "📊",
      permissionLevel: "view",
    },
    {
      path: "/phone-system/browser-call",
      label: "Browser Phone",
      icon: "📞",
      permissionLevel: "view",
    },
    {
      path: "/phone-system/recordings",
      label: "Call Recordings",
      icon: "🎙️",
      permissionLevel: "view",
    },
    {
      path: "/phone-system/numbers",
      label: "Phone Numbers",
      icon: "📱",
      permissionLevel: "edit",
    },
    {
      path: "/phone-system/assignments",
      label: "Number Assignments",
      icon: "🔗",
      permissionLevel: "edit",
    },
  ],
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },
  quickActions: [
    {
      label: "Purchase Number",
      path: "/phone-system",
      icon: "➕",
      description: "Purchase a new phone number",
    },
    {
      label: "View Recordings",
      path: "/phone-system/recordings",
      icon: "🎙️",
      description: "View call recordings",
    },
  ],

  // 📝 CHANGELOG
  changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready digital phone system",
                "✅ Browser-based calling with Twilio integration",
                "✅ Call recording management with transcriptions",
                "✅ Phone number purchasing and assignment",
                "✅ Comprehensive call dashboard and analytics",
                "✅ Multi-user phone number assignments",
                "✅ Complete call history and recordings"
            ],
            notes: "Official stable release with full digital phone system capabilities."
        },
        {
            version: "0.8.0",
            releaseDate: "2025-09-20",
            title: "Theme System Migration",
            features: [
                "✨ Full light/dark mode support (52 insertions)",
                "✨ Enhanced CallRecordings with theme awareness (40 lines)",
                "✨ Updated PhoneNumbersList color modes (30 lines)",
                "✨ Improved PhoneSystemDashboard themes (28 lines)",
                "✨ Consistent visual design system"
            ],
            notes: "Complete theme system implementation for better user experience."
        },
        {
            version: "0.7.0",
            releaseDate: "2025-09-13",
            title: "GraphQL Code Generation",
            features: [
                "✨ Added GraphQL codegen support",
                "✨ Fixed nullable field handling (17 deletions, 5 insertions)",
                "✨ Enhanced TypeScript type safety",
                "✨ Improved API integration for phone system"
            ],
            notes: "GraphQL integration improvements for better type safety."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-09-02",
            title: "Browser Call Enhancements",
            features: [
                "✨ Enhanced BrowserCall component (175 lines)",
                "✨ Added advanced call controls",
                "✨ Improved call quality monitoring",
                "✨ Better error handling and recovery"
            ],
            notes: "Advanced browser calling features and reliability improvements."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-09-01",
            title: "Dashboard Refactor & Call Management",
            features: [
                "🔨 Major PhoneSystemDashboard refactor (1,089 lines)",
                "✨ Enhanced call management (423 insertions, 666 deletions)",
                "✨ Improved user experience and navigation",
                "✨ Better performance and code organization"
            ],
            notes: "Comprehensive dashboard refactoring for better maintainability."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-08-31",
            title: "Call Recording Features",
            features: [
                "✨ Added sync functionality for call recordings (105 lines)",
                "✨ Implemented transcription feature (50 lines)",
                "✨ Enhanced UI for recordings (88 lines)",
                "✨ Improved PhoneNumberAssignmentAdmin (321 insertions)",
                "✨ Better visual consistency and usability"
            ],
            notes: "Advanced call recording capabilities with transcription support."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-08-30",
            title: "Number Assignment Management",
            features: [
                "✨ Created PhoneNumberAssignmentAdmin (466 lines)",
                "✨ Enhanced BrowserCall component (339 insertions)",
                "✨ Improved PhoneSystemDashboard integration (12 lines)",
                "🔨 Removed MakeOutboundCall component (355 deletions)",
                "✨ Updated dialpad to show by default"
            ],
            notes: "Phone number assignment features for multi-user management."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-08-27",
            title: "Branding & UI Consistency",
            features: [
                "✨ Enhanced BrowserCall branding (127 lines)",
                "✨ Improved CallRecordings UI (281 insertions)",
                "✨ Better MakeOutboundCall interface (130 lines)",
                "✨ Enhanced PhoneNumbersList presentation (197 lines)",
                "✨ Updated PhoneSystemDashboard design (235 lines)",
                "✨ Improved responsive design (228 insertions)",
                "✨ Enhanced brand configuration integration"
            ],
            notes: "Comprehensive UI improvements and brand consistency updates."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-08-23",
            title: "Initial Phone System Implementation",
            features: [
                "✅ Created CallRecordings component (556 lines)",
                "✅ Created PhoneNumbersList component (679 lines)",
                "✅ Created PhoneSystemDashboard (752 lines)",
                "✅ Created MakeOutboundCall component (272 lines)",
                "✅ Implemented BrowserCall with Twilio (341 lines)",
                "✅ WebSocket and region monitoring",
                "✅ Initial GraphQL integration",
                "✅ Module configuration and routing",
                "✅ Twilio API key integration"
            ],
            notes: "Initial implementation of the digital phone system with 1,284 lines of code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default phoneSystemModuleConfig;